import { useState, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import { dequal } from 'dequal';
import { GooglePlacePrediction } from '@type/v1-api-types/googleMaps';
import {
  Button, ComboBox, Label, RecordInterface, DatePickerInput, SetFormValues,
} from '@components/common/Forms';
import { MapIcon, MapPinIcon } from '@heroicons/react/24/outline';
import useGoogle from 'react-google-autocomplete/lib/usePlacesAutocompleteService';
import { showJobAddressMapModal } from '@components/templates/job-add/edit/map/JobAddressMapModal';

/** Props */
interface JobBlueCollarFieldsProps {
  record: RecordInterface,
  setFormValues: SetFormValues,
  googleApiKey: string,
  isEditMode: boolean,
  inActivePeriod?: boolean,
  initialInterviewAddress?: string,
  initialInterviewDateTime?: string,
  prefillAddress?: string,
}

/**
 * @description Blue collar job fields component for interview address and datetime.
 * These fields are mandatory for the blue bundle and can only be added when creating a new job.
 * When editing an existing job with blue bundle, these fields are visible but read-only if the job is active.
 * - `record` - record function from useBjForm
 * - `setFormValues` - setFormValues function from useBjForm for setting form values programmatically
 * - `googleApiKey` - Google API key for places autocomplete
 * - `isEditMode` - whether the job is being edited (true) or created (false)
 * - `inActivePeriod` - whether the job is currently in an active period
 * - `initialInterviewAddress` - initial interview address value (for edit mode)
 * - `initialInterviewDateTime` - initial interview date time value (for edit mode)
 * - `prefillAddress` - address to prefill from job addresses (for new jobs)
 */
export const JobBlueCollarFields = (props: JobBlueCollarFieldsProps) => {
  // Destructure props
  const {
    record,
    setFormValues,
    googleApiKey,
    isEditMode,
    inActivePeriod = false,
    initialInterviewAddress = '',
    initialInterviewDateTime = '',
    prefillAddress = '',
  } = props;

  // Translation
  const { t, i18n } = useTranslation();

  // State for interview date time
  const [interviewDateTime, setInterviewDateTime] = useState<Date | null>(
    initialInterviewDateTime ? new Date(initialInterviewDateTime.replace(' ', 'T')) : null,
  );

  // Minimum allowed date time (current time + 1 hour)
  const [minInterviewDateTime] = useState(() => {
    const date = new Date();
    date.setHours(date.getHours() + 1);
    return date;
  });

  // Determine if fields should be editable based on INITIAL state only (computed once on mount)
  // If the initial interview date time is null or in the past, fields remain editable until form submission
  const [isEditable] = useState(() => {
    if (!inActivePeriod) return true;
    if (!initialInterviewDateTime) return true;
    const initialDate = new Date(initialInterviewDateTime.replace(' ', 'T'));
    return initialDate < new Date();
  });

  // Disabled state: fields are disabled only if not editable
  const isDisabled = !isEditable;

  const formatInterviewDateTime = (date: Date | null) => {
    if (!date) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  // Track if prefill has been done to avoid repeated prefills
  const [hasBeenPrefilled, setHasBeenPrefilled] = useState<boolean>(false);

  // State for interview address (used for map modal)
  const [interviewAddress, setInterviewAddress] = useState<string>(initialInterviewAddress || '');

  // Google location hook
  const {
    placePredictions,
    getPlacePredictions,
  } = useGoogle({
    apiKey: googleApiKey,
    debounce: 300,
    language: i18n.language,
  });

  // Local state for predictions list - allows us to clear it after selection to close dropdown
  const [localPredictions, setLocalPredictions] = useState<typeof placePredictions>([]);

  // Update local predictions when Google returns new results
  useEffect(() => {
    if (!dequal(localPredictions, placePredictions)) {
      setLocalPredictions(placePredictions);
    }
  }, [placePredictions, localPredictions]);

  // Prefill interview address when blue bundle is selected and the field is empty (only for new jobs)
  // Uses setFormValues to properly set the form input value without interfering with ComboBox state
  useEffect(() => {
    if (!isDisabled && !isEditMode && prefillAddress && !hasBeenPrefilled) {
      setFormValues({ interviewAddress: prefillAddress });
      setInterviewAddress(prefillAddress);
      setHasBeenPrefilled(true);
    }
  }, [prefillAddress, isEditMode, isDisabled, hasBeenPrefilled, setFormValues]);

  // Sync internal state with prefillAddress when it changes, if not yet interacted and field is empty
  useEffect(() => {
    if (!isDisabled && !isEditMode && prefillAddress && !interviewAddress) {
      setFormValues({ interviewAddress: prefillAddress });
      setInterviewAddress(prefillAddress);
    }
  }, [prefillAddress, isEditMode, isDisabled, interviewAddress, setFormValues]);

  // Handle address change from map modal
  const handleAddressChange = (value: string) => {
    if (!isDisabled) {
      setFormValues({ interviewAddress: value });
      setInterviewAddress(value);
    }
  };

  // Render
  // **************************
  return (
    <div className="mt-4 w-full scroll-mt-20">
      {/* Interview Address */}
      <Label
        required
        description={t('job.add.label.interview.address')}
        descriptionClass="font-semibold"
      >
        {/*
          When these fields are visible but not editable (blue job in active period),
          the underlying input becomes disabled and would not be included in native form submission.
          Ensure values are still posted to the server to avoid validation failures.
        */}
        {isDisabled && (
          <input
            type="hidden"
            name="interviewAddress"
            value={interviewAddress || initialInterviewAddress || ''}
          />
        )}
        <div className="relative mt-1 flex items-start gap-2">
          <MapPinIcon
            className="absolute left-3 top-6 z-1 size-5 -translate-y-1/2 text-ink"
          />
          <ComboBox
            required
            disabled={isDisabled}
            closeOnBlur
            placeholder={t('job.add.label.interview.address.placeholder')}
            className={`grow text-ellipsis pl-10 pr-18 ${isDisabled ? 'cursor-not-allowed bg-surface-100' : ''}`}
            options={{
              valueFromKey: 'description',
              staticList: localPredictions,
            }}
            onInputValueChange={(inputValue) => {
              if (!isDisabled && inputValue && inputValue.length > 2) {
                getPlacePredictions({ input: inputValue });
              }
            }}
            onSelectedItemChange={(item) => {
              // Update local state for map modal when an item is selected
              if (!isDisabled && item && typeof item === 'object' && 'description' in item) {
                const address = (item as GooglePlacePrediction).description;
                setInterviewAddress(address);
                // Clear the predictions list to close the dropdown
                setLocalPredictions([]);
              }
            }}
            onBlur={(e) => {
              // Update local state for map modal when user finishes editing
              if (!isDisabled && e.target instanceof HTMLInputElement) {
                setInterviewAddress(e.target.value);
              }
            }}
            renderList={(
              item,
              index,
              listKey,
              getItemProps,
              listStyling,
              highlightedIndex,
            ) => {
              const place = item as GooglePlacePrediction;
              const highlightClass = highlightedIndex === index ? 'text-white' : 'text-ink-medium';
              return (
                <li key={listKey} className={`${listStyling} flex-col`} {...getItemProps({ item, index })}>
                  <span>{place?.structured_formatting?.main_text}</span>
                  <span className={`${highlightClass} -mt-0.5 text-tiny font-normal transition-all duration-150`}>
                    {place?.structured_formatting?.secondary_text}
                  </span>
                </li>
              );
            }}
            {...record('interviewAddress')}
          />
        </div>

        {/* Open map button */}
        <div className="mt-3 flex justify-end">
          <Button
            color="light"
            styling="soft"
            size="sm"
            rounding="full"
            disabled={isDisabled}
            className={`gap-2 !py-1.5 !pl-3.5 !pr-4 !font-normal leading-tight ${isDisabled ? 'cursor-not-allowed opacity-50' : ''}`}
            onClick={() => !isDisabled && showJobAddressMapModal(googleApiKey, interviewAddress, handleAddressChange)}
          >
            <MapIcon className="size-4" />
            {t('add.job.open.map')}
          </Button>
        </div>
      </Label>

      {/* Interview Date and Time */}
      <Label
        required
        description={t('job.add.label.interview.date.time')}
        descriptionClass="font-semibold"
      >
        {isDisabled && (
          <input
            type="hidden"
            name="interviewDateTime"
            value={initialInterviewDateTime || formatInterviewDateTime(interviewDateTime)}
          />
        )}
        <div className="mt-1">
          <DatePickerInput
            required
            disabled={isDisabled}
            date={interviewDateTime}
            setDate={(newDate) => {
              if (!isDisabled) {
                setInterviewDateTime(newDate);
              }
            }}
            placeholder={t('job.add.label.interview.date.time.placeholder')}
            className={isDisabled ? 'cursor-not-allowed bg-surface-100' : ''}
            pickerType="default"
            pickerProps={{
              showTimeSelect: true,
              timeFormat: 'HH:mm',
              timeIntervals: 15,
              dateFormat: 'yyyy-MM-dd HH:mm',
              minDate: minInterviewDateTime,
              ...(interviewDateTime && interviewDateTime.toDateString() === minInterviewDateTime.toDateString() && {
                minTime: minInterviewDateTime,
                maxTime: new Date(new Date(minInterviewDateTime).setHours(23, 59, 59, 999)),
              }),
            }}
            {...record('interviewDateTime')}
          />
        </div>
      </Label>
    </div>
  );
};
